import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import CompetitorCard from "@/pages/admin/market/CompetitorCard";
import useAdminCompetitors from "@/pages/admin/market/useAdminCompetitors";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "high", label: "High threat" },
  { id: "medium", label: "Watch" },
  { id: "low", label: "Low" },
];

export default function Competitors() {
  const navigate = useNavigate();
  const {
    key,
    competitors,
    status,
    fetchCompetitors,
    lock,
    saveNotes,
  } = useAdminCompetitors();

  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!key || status.state === "unauthorized") {
      navigate("/login?next=/admin/market/competitors", { replace: true });
    }
  }, [key, status.state, navigate]);

  const filtered = useMemo(() => {
    if (filter === "all") return competitors;
    return competitors.filter((c) => c.threat === filter);
  }, [competitors, filter]);

  const highCount = useMemo(
    () => competitors.filter((c) => c.threat === "high").length,
    [competitors]
  );

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-competitors-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            /{" "}
            <Link to="/admin/market" className="vbb-link" data-testid="crumb-market">
              Market
            </Link>{" "}
            / Competitors
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-competitors-title"
          >
            <BoxedTitle size="lg" thick>
              Competition.
            </BoxedTitle>
          </h1>
          <p className="mt-3 font-mono-tiny text-[#6b6b6b]">
            {status.state === "loading"
              ? "Loading…"
              : `${competitors.length} tracked · `}
            {status.state !== "loading" && (
              <span
                className={
                  highCount > 0 ? "text-[#e34234]" : "text-[#6b6b6b]"
                }
                data-testid="admin-high-threat-count"
              >
                {highCount} high threat
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/market"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-back-to-market"
          >
            ← Market
          </Link>
          <button
            onClick={() => fetchCompetitors(key)}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-refresh-btn"
          >
            Refresh
          </button>
          <button
            onClick={lock}
            className="vbb-btn"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      <div
        className="mt-8 flex flex-wrap gap-2"
        data-testid="competitor-filters"
      >
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`competitor-filter-${f.id}`}
            className={`px-4 py-2 font-mono-tiny uppercase tracking-wider border ${
              filter === f.id
                ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#fafafa]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {status.state === "error" && (
        <div
          className="mt-8 p-4 border border-[#e34234] text-[#e34234] font-mono-tiny"
          data-testid="admin-error-banner"
        >
          {status.error}
        </div>
      )}

      <div
        className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6"
        data-testid="competitor-grid"
      >
        {filtered.length === 0 && status.state === "ready" && (
          <div
            className="col-span-full p-10 text-center font-mono-tiny text-[#6b6b6b] border border-[#0a0a0a]"
            data-testid="competitor-empty-state"
          >
            No competitors match this filter.
          </div>
        )}
        {filtered.map((c) => (
          <CompetitorCard
            key={c.id}
            competitor={c}
            onSaveNotes={saveNotes}
          />
        ))}
      </div>
    </main>
  );
}
